import { useState } from "react";
import { COUNTRIES, validateEmail, validatePhone } from "./countries";
import CountrySelect from "./CountrySelect";
import LocationSelect from "./LocationSelect";
import formStyles from "./form.module.css";
import sharedStyles from "./checkout.module.css";
import styles from "./summary.module.css";
import Loader from "./Loader";
import TermsDisclaimer from "./TermsDisclaimer";

export default function DetailsStep({
  form,
  onChange,
  onSubmit,
  loading,
  error,
  onBack,
}) {
  const [touched, setTouched] = useState({});
  const emailError = touched.email && !validateEmail(form.email);
  const firstError = touched.firstName && form.firstName.trim().length < 1;
  const lastError = touched.lastName && form.lastName.trim().length < 1;
  const phoneError = touched.phone && !validatePhone(form.phone);
  const locationError = touched.location && !(form.location || "").trim();
  const selectedCountry =
    COUNTRIES.find((c) => c.iso === form.countryIso) || COUNTRIES[0];

  const handleSubmit = () => {
    setTouched({
      email: true,
      firstName: true,
      lastName: true,
      phone: true,
      location: true,
    });
    if (
      !validateEmail(form.email) ||
      form.firstName.trim().length < 1 ||
      form.lastName.trim().length < 1 ||
      !validatePhone(form.phone) ||
      !(form.location || "").trim()
    )
      return;
    onSubmit();
  };

  return (
    <div>
      <h2 className={styles.title}>Your details</h2>
      <p className={styles.subtitle} style={{ lineHeight: 1.6 }}>
        Your account is created only after payment is confirmed — no account, no
        charge if something goes wrong.
      </p>

      <div className={formStyles.noticeBox}>
        <span className={formStyles.noticeIcon}>🔒</span>
        <div className={formStyles.noticeText}>
          New to AwaSource? We'll set up your account automatically once payment
          succeeds and email your login details.{" "}
          <span className={formStyles.noticeStrong}>
            No account is created until payment is confirmed.
          </span>
        </div>
      </div>

      {/* Name row */}
      <div className={formStyles.nameRow}>
        <div>
          <label className={formStyles.label}>First name</label>
          <input
            type="text"
            placeholder="e.g. Henry"
            value={form.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
            className={`${formStyles.input} ${firstError ? formStyles.err : ""}`}
          />
          {firstError && (
            <div className={formStyles.fieldError}>Enter your first name.</div>
          )}
        </div>
        <div>
          <label className={formStyles.label}>Last name</label>
          <input
            type="text"
            placeholder="e.g. Dimo"
            value={form.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
            className={`${formStyles.input} ${lastError ? formStyles.err : ""}`}
          />
          {lastError && (
            <div className={formStyles.fieldError}>Enter your last name.</div>
          )}
        </div>
      </div>

      {/* Email */}
      <div className={formStyles.field}>
        <label className={formStyles.label}>Email address</label>
        <input
          type="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={(e) => onChange("email", e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, email: true }))}
          className={`${formStyles.input} ${emailError ? formStyles.err : ""}`}
        />
        {emailError && (
          <div className={formStyles.fieldError}>
            Please enter a valid email address.
          </div>
        )}
        <div className={formStyles.fieldHint}>
          Your confirmation and login details will be sent here.
        </div>
      </div>

      {/* Phone */}
      <div className={formStyles.field}>
        <label className={formStyles.label}>Phone number</label>
        <div className={formStyles.phoneRow}>
          <CountrySelect
            selected={selectedCountry}
            error={phoneError}
            onSelect={(c) => {
              onChange("countryIso", c.iso);
              onChange("dialCode", c.dial);
            }}
          />
          <input
            type="tel"
            inputMode="tel"
            placeholder="801 234 5678"
            value={form.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
            className={`${formStyles.input} ${formStyles.phoneInput} ${phoneError ? formStyles.err : ""}`}
          />
        </div>
        {phoneError && (
          <div className={formStyles.fieldError}>
            Enter a valid phone number.
          </div>
        )}
        <div className={formStyles.fieldHint}>
          Used for payment and account verification only.
        </div>
      </div>

      {/* Location */}
      <div className={formStyles.fieldLast}>
        <label className={formStyles.label}>Location</label>
        <LocationSelect
          value={form.location}
          error={locationError}
          onOpen={() => setTouched((t) => ({ ...t, location: true }))}
          onSelect={(c) => {
            onChange("location", c.name);
            onChange("locationCountryIso", c.iso);
          }}
        />
        {locationError && (
          <div className={formStyles.fieldError}>Select your country.</div>
        )}
        <div className={formStyles.fieldHint}>
          Available in Nigeria, the UK and Europe.
        </div>
      </div>

      {error && <div className={formStyles.errorBanner}>{error}</div>}

      <TermsDisclaimer />
      <button
        onClick={handleSubmit}
        disabled={loading}
        className={sharedStyles.btnPrimary}
        style={{ marginBottom: 12 }}
      >
        {loading ? (
          <>
            <Loader /> Preparing payment…
          </>
        ) : (
          "Proceed to payment →"
        )}
      </button>

      <p className={formStyles.consent}>
        By continuing you agree to AwaSource's{" "}
        <a href="/terms" target="_blank" rel="noopener">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy" target="_blank" rel="noopener">
          Privacy Policy
        </a>
        .
      </p>

      <button onClick={onBack} className={sharedStyles.btnGhost}>
        ← Back
      </button>
    </div>
  );
}
